import type { AuditEntry } from "@divespots/contracts";
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITIES,
  AuditAction,
  AuditEntity,
  Id,
  OBJECT_TYPES,
  SUGGESTION_KINDS,
} from "@divespots/contracts";

/** The log's filters; they live in the URL query (`?entity=…&action=…&actorId=…&spotId=…`). */
export interface AuditFilters {
  entity?: AuditEntity;
  action?: AuditAction;
  actorId?: string;
  spotId?: string;
}

export const AUDIT_PAGE = 25;

/** Reads the filters from a query string; anything that is not valid is dropped, never trusted. */
export function parseAuditFilters(sp: URLSearchParams): AuditFilters {
  const out: AuditFilters = {};
  const entity = AuditEntity.safeParse(sp.get("entity"));
  if (entity.success) out.entity = entity.data;
  const action = AuditAction.safeParse(sp.get("action"));
  if (action.success) out.action = action.data;
  const actorId = sp.get("actorId");
  if (actorId && actorId.length <= 64) out.actorId = actorId;
  const spotId = Id.safeParse(sp.get("spotId"));
  if (spotId.success) out.spotId = spotId.data;
  return out;
}

/** The URL query for the filters, in a fixed key order (no empty values). */
export function auditFiltersToParams(f: AuditFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.entity) sp.set("entity", f.entity);
  if (f.action) sp.set("action", f.action);
  if (f.actorId) sp.set("actorId", f.actorId);
  if (f.spotId) sp.set("spotId", f.spotId);
  return sp;
}

/** The API query for the filters and an optional cursor. */
export function auditQuery(f: AuditFilters, before?: string): Record<string, string> {
  return {
    ...Object.fromEntries(auditFiltersToParams(f)),
    limit: String(AUDIT_PAGE),
    ...(before ? { before } : {}),
  };
}

export { AUDIT_ACTIONS, AUDIT_ENTITIES };

const I18N_KEYS = ["de", "en", "fr", "it"];

/** A diff value as short readable text: i18n objects as "de: … · en: …", other objects as compact JSON. */
export function formatAuditValue(v: unknown): string {
  if (v === null || v === undefined) return "–";
  if (typeof v === "string") return v === "" ? "“”" : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) {
    return v.every((x) => x === null || ["string", "number", "boolean"].includes(typeof x))
      ? v.map(formatAuditValue).join(", ") || "–"
      : JSON.stringify(v);
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o);
    if (keys.length > 0 && keys.every((k) => I18N_KEYS.includes(k) && typeof o[k] === "string")) {
      const parts = keys.filter((k) => o[k] !== "").map((k) => `${k}: ${o[k] as string}`);
      return parts.length ? parts.join(" · ") : "–";
    }
    return JSON.stringify(v);
  }
  return String(v);
}

export const TRUNCATE_AT = 80;

/** Cuts long text with an ellipsis; `truncated` says whether a "more" control is needed. */
export function truncateText(s: string, max = TRUNCATE_AT): { text: string; truncated: boolean } {
  if (s.length <= max) return { text: s, truncated: false };
  return { text: `${s.slice(0, max - 1).trimEnd()}…`, truncated: true };
}

/** Where a row's target links to (admin paths without the language prefix), or null. */
export function auditTargetPath(e: AuditEntry): string | null {
  switch (e.entity) {
    case "suggestion":
      return `/admin/vorschlaege/${e.suggestionId ?? e.entityId}`;
    case "spot":
    case "object":
    case "sighting":
      return !e.target.deleted && e.target.spotId ? `/admin/spots/${e.target.spotId}` : null;
    case "user":
      return e.target.deleted ? null : "/admin/users";
    case "lake":
      return e.target.deleted ? null : "/admin/lakes";
    case "settings":
      return "/admin/settings";
  }
}

/** The "Vorschlag" link of an approval-written spot or object row. */
export function auditSuggestionPath(e: AuditEntry): string | null {
  return e.entity !== "suggestion" && e.suggestionId
    ? `/admin/vorschlaege/${e.suggestionId}`
    : null;
}

export type AuditNote =
  | { kind: "text"; text: string }
  | { kind: "adjusted" }
  | { kind: "cascaded" }
  | { kind: "redacted" }
  | { kind: "submitKind"; value: string }
  | { kind: "reason"; text: string };

/** One-line summary of a row that has no field changes (reject reason, submitted kind, …). */
export function auditNotes(e: AuditEntry): AuditNote[] {
  const d = e.diff;
  if (d === null || typeof d !== "object" || Array.isArray(d)) return [];
  const o = d as Record<string, unknown>;
  const notes: AuditNote[] = [];
  if (o.redacted === true) notes.push({ kind: "redacted" });
  if (e.action === "submit" && typeof o.kind === "string")
    notes.push({ kind: "submitKind", value: o.kind });
  if (e.action === "approve" && o.adjusted === true) notes.push({ kind: "adjusted" });
  if (
    (e.action === "reject" || e.action === "withdraw") &&
    typeof o.reason === "string" &&
    o.reason
  )
    notes.push({ kind: "reason", text: o.reason });
  if (e.action === "delete" && o.cascadedFromSpot) notes.push({ kind: "cascaded" });
  return notes;
}

/** The i18n key suffix of a diff field: dots become "_" (`model.scale` → `model_scale`). */
export function auditFieldKey(field: string): string {
  return field.replace(/\./g, "_");
}

const OBJECT_TYPE_VALUES = new Set(OBJECT_TYPES as readonly string[]);

/** Existing catalog key that names a diff value (status, type, role, kind), or null for plain values. */
export function auditValueKey(field: string, v: unknown): string | null {
  if (typeof v !== "string") return null;
  const leaf = field.split(".").at(-1);
  if (leaf === "status") {
    if (v === "draft" || v === "published") return `admin.status.${v}`;
    if (["pending", "approved", "rejected", "withdrawn"].includes(v)) return `suggest.status.${v}`;
  }
  if (leaf === "type" && OBJECT_TYPE_VALUES.has(v)) return `objects.type.${v}`;
  if (leaf === "role" && ["admin", "moderator", "diver"].includes(v)) return `admin.role.${v}`;
  if (leaf === "kind" && (SUGGESTION_KINDS as readonly string[]).includes(v))
    return `suggest.kind.${v}`;
  return null;
}
